import React from 'react';
import { FunnelIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AdminFilterCard } from '../AdminSurface';
import { InvoiceDisplayStatus } from '../../types/invoice';
import { DueDateFilter, InvoiceTypeFilter } from '../../types/billingUi';
import { statusLabel } from '../../utils/invoiceFormatting';
import CustomerTypeahead, { CustomerTypeaheadOption } from '../customer/CustomerTypeahead';

type FilterChip = {
  id: string;
  label: string;
  onClear: () => void;
};

type Props = {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  selectedCustomerFilter: CustomerTypeaheadOption | null;
  setSelectedCustomerFilter: (value: CustomerTypeaheadOption | null) => void;
  dueDateFilter: DueDateFilter;
  setDueDateFilter: (value: DueDateFilter) => void;
  invoiceTypeFilter: InvoiceTypeFilter;
  setInvoiceTypeFilter: (value: InvoiceTypeFilter) => void;
  minAmountFilter: string;
  setMinAmountFilter: (value: string) => void;
  maxAmountFilter: string;
  setMaxAmountFilter: (value: string) => void;
  activeFilterChips: FilterChip[];
  onClearFilters: () => void;
};

export default function InvoiceFilterBar({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  selectedCustomerFilter,
  setSelectedCustomerFilter,
  dueDateFilter,
  setDueDateFilter,
  invoiceTypeFilter,
  setInvoiceTypeFilter,
  minAmountFilter,
  setMinAmountFilter,
  maxAmountFilter,
  setMaxAmountFilter,
  activeFilterChips,
  onClearFilters,
}: Props) {
  return (
    <AdminFilterCard
      title="Filters"
      description="Search and compose filters by lifecycle, customer scope, due date, invoice type, and amount."
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          <div className="relative xl:col-span-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="text"
              placeholder="Search invoice #, customer, or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <div className="xl:col-span-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field">
              <option value="all">All statuses</option>
              <option value="draft">{statusLabel('draft' as InvoiceDisplayStatus)}</option>
              <option value="open">{statusLabel('open' as InvoiceDisplayStatus)}</option>
              <option value="pending payment">{statusLabel('pending payment')}</option>
              <option value="paid">{statusLabel('paid' as InvoiceDisplayStatus)}</option>
              <option value="overdue">{statusLabel('overdue')}</option>
              <option value="void">{statusLabel('void' as InvoiceDisplayStatus)}</option>
              <option value="blocked">{statusLabel('blocked')}</option>
            </select>
          </div>
          <div className="xl:col-span-2">
            <CustomerTypeahead
              value={selectedCustomerFilter}
              onChange={setSelectedCustomerFilter}
              placeholder="All customers"
            />
          </div>
          <div className="xl:col-span-2">
            <select value={dueDateFilter} onChange={(e) => setDueDateFilter(e.target.value as DueDateFilter)} className="input-field">
              <option value="all">All due dates</option>
              <option value="overdue">Overdue</option>
              <option value="next_7_days">Due in 7 days</option>
              <option value="no_due_date">No due date</option>
            </select>
          </div>
          <div className="xl:col-span-2">
            <select value={invoiceTypeFilter} onChange={(e) => setInvoiceTypeFilter(e.target.value as InvoiceTypeFilter)} className="input-field">
              <option value="all">All invoice types</option>
              <option value="grouped">Grouped Payment</option>
              <option value="single">Single invoices</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {/* <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Min amount"
              value={minAmountFilter}
              onChange={(e) => setMinAmountFilter(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <div>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Max amount"
              value={maxAmountFilter}
              onChange={(e) => setMaxAmountFilter(e.target.value)}
              className="input-field"
            />
          </div> */}
          <button type="button" className="btn-secondary" onClick={onClearFilters}>
            Clear filters
          </button>
        </div>

        {activeFilterChips.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={chip.onClear}
                className="inline-flex items-center gap-1 rounded-full border border-outline/70 bg-surface-container-low px-3 py-1 text-xs font-medium text-on-surface"
              >
                {chip.label}
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
        )}
      </div>
    </AdminFilterCard>
  );
}
